import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import { gsap } from 'gsap';
import { getProductById, getRandomProducts } from '../data/products';
import ProductCard from '../components/ProductCard';
import DocumentDownload from '../components/DocumentDownload';
import WeightSelector from '../components/WeightSelector';
import { ProductVariant } from '../types';
import ReviewsSection from '../components/reviews/ReviewsSection';
import { REVIEWS_ENABLED } from '../config/features';
import ContactOptionsModal from '../components/ContactOptionsModal';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  // Для свайпов на мобильных
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  const product = getProductById(id || '');
  const recommendedProducts = getRandomProducts(id, 3);
  
  // Получаем текущие изображения и цену в зависимости от выбранного варианта
  const currentImages = selectedVariant?.images || product?.images || [product?.image];
  const currentPrice = useMemo(() => {
    return selectedVariant?.price || product?.price;
  }, [selectedVariant?.price, product?.price]);
  const currentImage = currentImages[currentImageIndex] || selectedVariant?.image || product?.image;
  
  const imageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const specsRef = useRef<HTMLDivElement>(null);
  const recommendedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!product) {
      navigate('/products');
      return;
    }

    // Анимации появления
    const elements = [
      { ref: imageRef, delay: 0 },
      { ref: contentRef, delay: 0.2 },
      { ref: featuresRef, delay: 0.4 },
      { ref: specsRef, delay: 0.6 },
      { ref: recommendedRef, delay: 0.8 }
    ];

    elements.forEach(({ ref, delay }) => {
      if (ref.current) {
        gsap.fromTo(ref.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay,
            ease: 'power2.out'
          }
        );
      }
    });
  }, [product, navigate]);

  // Инициализация selectedVariant при загрузке товара
  useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      // Сбрасываем selectedVariant при смене товара
      setSelectedVariant(null);
      // Выбираем первый доступный вариант или первый вариант
      const availableVariant = product.variants.find(v => v.inStock) || product.variants[0];
      setSelectedVariant(availableVariant);
    } else {
      setSelectedVariant(null);
    }
  }, [product?.id]); // Зависимость от id товара, чтобы сбрасывать при смене

  // Сброс индекса изображения при изменении варианта
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedVariant]);

  if (!product) {
    return null;
  }
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % currentImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + currentImages.length) % currentImages.length);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  // Определяем предыдущую страницу
  const getPreviousPage = () => {
    const referrer = document.referrer;
    const fromShop = location.state?.from === 'shop' || referrer.includes('/shop');
    const fromProducts = location.state?.from === 'products' || referrer.includes('/products');
    
    if (fromShop) return '/shop';
    if (fromProducts) return '/products';
    
    // По умолчанию возвращаемся на /products
    return '/products';
  };

  const nextFeature = () => {
    setCurrentFeatureIndex((prev) => (prev + 1) % (product?.features?.length || 1));
  };

  const prevFeature = () => {
    setCurrentFeatureIndex((prev) => (prev - 1 + (product?.features?.length || 1)) % (product?.features?.length || 1));
  };

  // Функции для свайпов изображений
  const onImageTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onImageTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onImageTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  // Функции для свайпов особенностей
  const onFeatureTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onFeatureTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onFeatureTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextFeature();
    } else if (isRightSwipe) {
      prevFeature();
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Кнопка "Назад" */}
        <button
          onClick={() => navigate(getPreviousPage())}
          className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-8 transition-all duration-300 hover:scale-105 group"
        >
          <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
          <span>Вернуться к продукции</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Галерея изображений */}
          <div ref={imageRef} className="space-y-4">
            {/* Изображение */}
            <div className="relative">
              <div 
                className="relative overflow-hidden rounded-xl product-image-container-mobile md:product-image-container-desktop product-image-carousel"
                onTouchStart={onImageTouchStart}
                onTouchMove={onImageTouchMove}
                onTouchEnd={onImageTouchEnd}
              >
                <img
                  src={currentImage}
                  alt={product.name}
                  className="w-full product-image-mobile md:h-[400px]"
                  key={currentImageIndex}
                />
                
                {/* Индикатор наличия */}
                {!product.inStock && (
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Под заказ
                  </div>
                )}
                
                {/* Счетчик изображений */}
                {currentImages.length > 1 && (
                  <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm">
                    {currentImageIndex + 1} / {currentImages.length}
                  </div>
                )}
                

                

              </div>

              {/* Кнопки навигации */}
              {currentImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 md:left-6 top-1/2 transform -translate-y-1/2 p-2 md:p-3 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                    aria-label="Предыдущее изображение"
                  >
                    <ChevronLeft className="w-6 h-6 md:w-8 md:h-8 text-gray-700 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 md:right-6 top-1/2 transform -translate-y-1/2 p-2 md:p-3 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                    aria-label="Следующее изображение"
                  >
                    <ChevronRight className="w-6 h-6 md:w-8 md:h-8 text-gray-700 dark:text-gray-300" />
                  </button>
                </>
              )}
            </div>

            {/* Миниатюры */}
            {currentImages.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto">
                {currentImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      index === currentImageIndex
                        ? 'border-primary-500'
                        : 'border-gray-200 dark:border-gray-600 hover:border-primary-300'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} - миниатюра ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Индикатор */}
            {currentImages.length > 1 && (
              <div className="flex justify-center space-x-2">
                {currentImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                      index === currentImageIndex
                        ? 'bg-primary-500'
                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-primary-300'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Информация о продукте */}
          <div ref={contentRef} className="space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {product.name}
              </h1>
              
              {currentPrice && (
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-4">
                  {formatPrice(currentPrice)} ₽
                </p>
              )}

              {/* Описание товара с возможностью сворачивания на мобильных */}
              <div className="space-y-3">
                <p className={`text-lg text-gray-600 dark:text-gray-300 leading-relaxed ${
                  isDescriptionExpanded ? '' : 'line-clamp-3 md:line-clamp-none'
                }`}>
                  {product.fullDescription || product.description}
                </p>
                
                {/* Кнопка сворачивания/разворачивания только для мобильных */}
                <div className="md:hidden">
                  <button
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    className="text-gray-600 dark:text-gray-300 text-sm font-medium hover:text-gray-800 dark:hover:text-gray-100 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span>{isDescriptionExpanded ? 'Свернуть' : 'Читать далее'}</span>
                    {isDescriptionExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Кнопка скачивания документов */}
            <div className="mb-6">
              <DocumentDownload productId={product.id} />
            </div>



            {/* Выбор веса */}
            {product.variants && product.variants.length > 0 && (
              <div className="mb-6">
                <WeightSelector
                  variants={product.variants}
                  selectedVariant={selectedVariant}
                  onVariantChange={setSelectedVariant}
                />
              </div>
            )}

            {/* Добавление в корзину */}
            {(() => {
              // Проверяем, есть ли товар в наличии
              const isInStock = selectedVariant ? selectedVariant.inStock : product.inStock;
              
              if (!isInStock) {
                return (
                  <div className="mb-6">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                      <p className="text-red-800 dark:text-red-200 font-medium">
                        Для приобретения свяжитесь с нами удобным для вас способом
                      </p>
                    </div>
                  </div>
                );
              }
              
              return (
                <div className="mb-6">
                  <button 
                    onClick={() => setIsContactModalOpen(true)}
                    disabled={!selectedVariant && !product.price}
                    className="btn-primary inline-flex items-center space-x-2 w-full lg:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Phone className="w-5 h-5" />
                    <span>Связаться</span>
                  </button>
                </div>
              );
            })()}
          </div>
        </div>

        {REVIEWS_ENABLED && <ReviewsSection product={product} />}

        {/* Особенности и преимущества */}
        {product.features && product.features.length > 0 && (
          <div ref={featuresRef} className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Особенности и преимущества
            </h2>
            
            {/* Мобильная версия - свайпаемый слайдер */}
            <div className="md:hidden">
              <div className="relative">
                <div 
                  className="overflow-hidden"
                  onTouchStart={onFeatureTouchStart}
                  onTouchMove={onFeatureTouchMove}
                  onTouchEnd={onFeatureTouchEnd}
                >
                  <div className="flex transition-all duration-500 ease-out" style={{ transform: `translateX(-${currentFeatureIndex * 100}%)` }}>
                    {product.features.map((feature, index) => (
                      <div key={index} className="w-full flex-shrink-0 px-4">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center">
                          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="text-gray-700 dark:text-gray-300 text-lg">{feature}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Индикаторы */}
              {product.features.length > 1 && (
                <div className="flex justify-center space-x-2 mt-6">
                  {product.features.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentFeatureIndex(index)}
                      className={`feature-indicator w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentFeatureIndex
                          ? 'bg-primary-600 dark:text-primary-400 active'
                          : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                      }`}
                      aria-label={`Перейти к особенности ${index + 1}`}
                    />
                  ))}
                </div>
              )}
              
              {/* Подсказка о свайпах и счетчик */}
              {product.features.length > 1 && (
                <div className="text-center mt-4 space-y-2">
                  <div className="text-gray-500 dark:text-gray-400 text-sm">
                    <span>{currentFeatureIndex + 1} из {product.features.length}</span>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center justify-center space-x-2">
                    <span className="inline-block w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded-full animate-pulse"></span>
                    <span>Свайп влево/вправо для навигации</span>
                    <span className="inline-block w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded-full animate-pulse"></span>
                  </p>
                </div>
              )}
            </div>
            
            {/* Десктопная версия - сетка */}
            <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {product.features.map((feature, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Характеристики */}
        <div ref={specsRef} className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Характеристики
          </h2>
          
          {/* Мобильная версия - раскрывающиеся секции */}
          <div className="md:hidden">
            <div className="space-y-3">
              {/* Состав */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Состав</h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex flex-col space-y-2">
                    {product.id === '1' ? (
                      <div className="text-xs">
                        <p className="text-gray-900 dark:text-white">Органическое вещество 87%, Азот 3,7%, Фосфор 5,7%, Калий 3,4%, Свинец (валовая форма) 4,37 мг/кг, Мышьяк 0,4 млн, Ртуть менее 0,015 мг/кг, Гуминовые кислоты 26%, Сухое вещество 89,0%, Влажность (валовая форма) 10%, Кадмий (валовая форма) 0,2 мг/кг.</p>
                        <p className="text-gray-900 dark:text-white mt-2 text-xs italic">Данные предоставлены согласно протоколу испытания N 8a от 25.03.2025г.</p>
                      </div>
                    ) : product.id === '2' ? (
                      <div className="text-xs">
                        <p className="text-gray-900 dark:text-white">Зоогумус, личинка чёрной львинки, отмор мухи чёрная львинка.</p>
                      </div>
                    ) : product.id === '3' ? (
                      <>
                        <div className="text-xs">
                          <p className="text-gray-900 dark:text-white">Сушеные личинки черной львинки (Black Soldier Fly).</p>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Дополнительные компоненты:</span>
                          <p className="text-gray-900 dark:text-white mt-1">Метионин, витамин B1, натрий, фосфор.</p>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Аминокислотный состав:</span>
                          <p className="text-gray-900 dark:text-white mt-1">аланин, аргинин, аспарагиновая кислота, глутаминовая кислота, глицин, изолейцин, лейцин, лизин, метионин, пролин, серин, тирозин, треонин, триптофан, фенилаланин, цистин.</p>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Витамины и минералы:</span>
                          <p className="text-gray-900 dark:text-white mt-1">витамин B2 (рибофлавин), витамин B3, витамин B5 (никотинамид), витамин B9 (фолиевая кислота), калий, кальций, магний, натрий.</p>
                        </div>
                        <p className="text-gray-900 dark:text-white mt-2 text-xs italic">Данные предоставлены согласно протоколу испытания No 9199.24 АВ от 05.08.2024.</p>
                      </>
                    ) : product.id === '4' ? (
                      <>
                        <div className="text-xs">
                          <p className="text-gray-900 dark:text-white">Сушеные личинки черной львинки (Black Soldier Fly).</p>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Дополнительные компоненты:</span>
                          <p className="text-gray-900 dark:text-white mt-1">Метионин, витамин B1, натрий, фосфор.</p>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Аминокислотный состав:</span>
                          <p className="text-gray-900 dark:text-white mt-1">аланин, аргинин, аспарагиновая кислота, глутаминовая кислота, глицин, изолейцин, лейцин, лизин, метионин, пролин, серин, тирозин, треонин, триптофан, фенилаланин, цистин.</p>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Витамины и минералы:</span>
                          <p className="text-gray-900 dark:text-white mt-1">витамин B2 (рибофлавин), витамин B3, витамин B5 (никотинамид), витамин B9 (фолиевая кислота), калий, кальций, магний, натрий.</p>
                        </div>
                        <p className="text-gray-900 dark:text-white mt-2 text-xs italic">Данные предоставлены согласно протоколу испытания No 9199.24 АВ от 05.08.2024.</p>
                      </>
                    ) : product.id === '5' ? (
                      <>
                        <div className="text-xs">
                          <p className="text-gray-900 dark:text-white">Аминокислотный состав:</p>
                          <p className="text-gray-900 dark:text-white mt-1">Аланин, аргинин, аспарагиновая кислота, валин, гистидин, глутаминовая кислота, глицин, изолейцин, лейцин, лизин, метионин, пролин, серин, тирозин, треонин, триптофан, фенилаланин, цистин.</p>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Витамины и минералы:</span>
                          <p className="text-gray-900 dark:text-white mt-1">Витамин A, витамин D, витамин E, витамин B1, витамин B2 (рибофлавин), витамин B3, витамин B5 (никотинамид), витамин B9 (фолиевая кислота), калий, кальций, магний, натрий, фосфор.</p>
                        </div>
                        <div className="text-xs">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Иные показатели:</span>
                          <p className="text-gray-900 dark:text-white mt-1">Жир 7,99 %, зола 2,4 %, сырой протеин 23,49 %, сырая клетчатка 4,6 %, легкогидролизуемые углеводы 43,5 %, растворимые углеводы (сахара) менее 0,3 %.</p>
                          <p className="text-gray-900 dark:text-white mt-1">Массовая доля влаги составляет 11,7 %.</p>
                        </div>
                        <p className="text-gray-900 dark:text-white mt-2 text-xs italic">Данные предоставлены согласно протоколу испытания.</p>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Условия хранения */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Условия хранения</h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex flex-col space-y-2">
                    <div className="text-xs">
                      <span className="font-medium text-gray-600 dark:text-gray-400">Условия хранения:</span>
                      <p className="text-gray-900 dark:text-white mt-1">
                        {product.id === '1' ? 'В сухом месте, защищенном от прямого солнца' :
                         product.id === '2' ? 'В сухом месте, без доступа прямых солнечных лучей' :
                         product.id === '5' ? 'В сухом месте, защищенном от прямых солнечных лучей' : ''}
                      </p>
                    </div>
                    <div className="text-xs">
                      <span className="font-medium text-gray-600 dark:text-gray-400">Температура:</span>
                      <p className="text-gray-900 dark:text-white mt-1">
                        {product.id === '1' ? '±30°C' :
                         product.id === '2' ? 'до ±30°C' :
                         product.id === '5' ? '±30°C' : ''}
                      </p>
                    </div>
                    <div className="text-xs">
                      <span className="font-medium text-gray-600 dark:text-gray-400">Срок годности:</span>
                      <p className="text-gray-900 dark:text-white mt-1">
                        {product.id === '1' ? '1,5 года с даты упаковки' :
                         product.id === '2' ? '1,5 года' :
                         product.id === '5' ? '1,5 года с даты упаковки' : ''}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Десктопная версия - улучшенный дизайн */}
          <div className="hidden md:block">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Состав */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Состав</h3>
                <div className="space-y-4">
                  {product.id === '1' ? (
                    <div>
                      <p className="text-sm text-gray-900 dark:text-white">Органическое вещество 87%, Азот 3,7%, Фосфор 5,7%, Калий 3,4%, Свинец (валовая форма) 4,37 мг/кг, Мышьяк 0,4 млн, Ртуть менее 0,015 мг/кг, Гуминовые кислоты 26%, Сухое вещество 89,0%, Влажность (валовая форма) 10%, Кадмий (валовая форма) 0,2 мг/кг.</p>
                      <p className="text-sm text-gray-900 dark:text-white mt-2 text-xs italic">Данные предоставлены согласно протоколу испытания N 8a от 25.03.2025г.</p>
                    </div>
                  ) : product.id === '2' ? (
                    <div>
                      <p className="text-sm text-gray-900 dark:text-white">Зоогумус, личинка чёрной львинки, отмор мухи чёрная львинка.</p>
                    </div>
                  ) : product.id === '3' ? (
                    <>
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white">Сушеные личинки черной львинки (Black Soldier Fly).</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Дополнительные компоненты:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">Метионин, витамин B1, натрий, фосфор.</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Аминокислотный состав:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">аланин, аргинин, аспарагиновая кислота, глутаминовая кислота, глицин, изолейцин, лейцин, лизин, метионин, пролин, серин, тирозин, треонин, триптофан, фенилаланин, цистин.</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Витамины и минералы:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">витамин B2 (рибофлавин), витамин B3, витамин B5 (никотинамид), витамин B9 (фолиевая кислота), калий, кальций, магний, натрий.</p>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-white mt-2 text-xs italic">Данные предоставлены согласно протоколу испытания No 9199.24 АВ от 05.08.2024.</p>
                    </>
                  ) : product.id === '4' ? (
                    <>
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white">Сушеные личинки черной львинки (Black Soldier Fly).</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Дополнительные компоненты:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">Метионин, витамин B1, натрий, фосфор.</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Аминокислотный состав:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">аланин, аргинин, аспарагиновая кислота, глутаминовая кислота, глицин, изолейцин, лейцин, лизин, метионин, пролин, серин, тирозин, треонин, триптофан, фенилаланин, цистин.</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Витамины и минералы:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">витамин B2 (рибофлавин), витамин B3, витамин B5 (никотинамид), витамин B9 (фолиевая кислота), калий, кальций, магний, натрий.</p>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-white mt-2 text-xs italic">Данные предоставлены согласно протоколу испытания No 9199.24 АВ от 05.08.2024.</p>
                    </>
                  ) : product.id === '5' ? (
                    <>
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white">Аминокислотный состав:</p>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">Аланин, аргинин, аспарагиновая кислота, валин, гистидин, глутаминовая кислота, глицин, изолейцин, лейцин, лизин, метионин, пролин, серин, тирозин, треонин, триптофан, фенилаланин, цистин.</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Витамины и минералы:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">Витамин A, витамин D, витамин E, витамин B1, витамин B2 (рибофлавин), витамин B3, витамин B5 (никотинамид), витамин B9 (фолиевая кислота), калий, кальций, магний, натрий, фосфор.</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Иные показатели:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">Жир 7,99 %, зола 2,4 %, сырой протеин 23,49 %, сырая клетчатка 4,6 %, легкогидролизуемые углеводы 43,5 %, растворимые углеводы (сахара) менее 0,3 %.</p>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">Массовая доля влаги составляет 11,7 %.</p>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-white mt-2 text-xs italic">Данные предоставлены согласно протоколу испытания.</p>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Условия хранения */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Условия хранения</h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Условия хранения:</span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {product.id === '1' ? 'В сухом месте, защищенном от прямого солнца' :
                       product.id === '2' ? 'В сухом месте, без доступа прямых солнечных лучей' :
                       product.id === '5' ? 'В сухом месте, защищенном от прямых солнечных лучей' : ''}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Температура:</span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {product.id === '1' ? '±30°C' :
                       product.id === '2' ? 'до ±30°C' :
                       product.id === '5' ? '±30°C' : ''}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Срок годности:</span>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {product.id === '1' ? '1,5 года с даты упаковки' :
                       product.id === '2' ? '1,5 года' :
                       product.id === '5' ? '1,5 года с даты упаковки' : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Рекомендуемые товары */}
        {recommendedProducts.length > 0 && (
          <div ref={recommendedRef} className="hidden md:block mt-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Вам также может понравиться
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recommendedProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          </div>
        )}
      </div>
      <ContactOptionsModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        subtitle="Покупка на сайте временно отключена. Свяжитесь с нами напрямую, чтобы уточнить наличие и условия приобретения."
      />
    </div>
  );
};

export default ProductDetail;
